import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CircleAlert } from "lucide-react";
import { useCancelPlanContext } from "./cancel-provider";
import { format } from "date-fns";
import { getPriceForThePlan } from "@/lib/plan.utils";
import { PaymentInterval } from "@/types/subscription.types";

const CancelConfirmation = () => {
  const { state, planDetails, subscription, searchParams, handleStateChange } =
    useCancelPlanContext();

  if (!planDetails || !subscription) {
    return <div>No plan details or subscription found</div>;
  }

  const renderDecreasedPriceBasedOnInterval = () => {
    const price =
      getPriceForThePlan({
        planType: planDetails?.planId?.planType,
        planDetails: planDetails,
        subscription: subscription,
      }) || 0;
    const perMonthPrice = price * searchParams.quantity;

    if (subscription.interval === PaymentInterval.ANNUAL) {
      return (perMonthPrice * 12).toFixed(2);
    }

    if (subscription.interval === PaymentInterval.QUARTERLY) {
      return (perMonthPrice * 3).toFixed(2);
    }

    return perMonthPrice.toFixed(2);
  };

  const handleBack = () => handleStateChange({ step: state.step - 1 });
  const handleConfirm = () => handleStateChange({ showConfirmationDialog: true });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Confirm Cancellation</CardTitle>
        <CardDescription>
          Please review the information below before confirming
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-900/20 rounded-lg p-4">
            <h4 className="font-semibold text-red-800 dark:text-red-400 mb-2">
              Cancellation Summary
            </h4>
            <ul className="space-y-2 text-red-700 dark:text-red-400">
              <li className="flex items-center gap-2">
                <CircleAlert className="h-4 w-4 shrink-0" />
                <span>
                  Your{" "}
                  {planDetails.planId?.planName ||
                    planDetails.addonPlanId?.name}{" "}
                  plan will be canceled and remain active until{" "}
                  <strong>{format(subscription.endDate, "MMM d, yyyy")}</strong>
                  .
                </span>
              </li>
              <li className="flex items-center gap-2">
                <CircleAlert className="h-4 w-4 shrink-0" />
                <span>
                  After this date, you will lose access to all features
                  including {planDetails.maxWarmupEmailsDaily} warmup emails/day
                  and {planDetails.maxWarmupEmailsMonthly} warmup emails/month.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <CircleAlert className="h-4 w-4 shrink-0" />
                <span>
                  No further charges ($
                  {renderDecreasedPriceBasedOnInterval()}/
                  {subscription.interval.toLowerCase()}) will be applied after
                  cancellation.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <CircleAlert className="h-4 w-4 shrink-0" />
                <span>
                  Once plan is cancelled you will not be able to access your
                  plan.
                </span>
              </li>
            </ul>
          </div>

          <Separator />
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={handleBack}>
          Back
        </Button>
        <Button variant="destructive" onClick={handleConfirm}>
          Confirm Cancellation
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CancelConfirmation;
